import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, X, Copy, FileDown, Trash2, Check, CheckSquare, Square, Save } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const ConversationsModal = ({ 
  isOpen, 
  onClose,
  conversations = [], 
  currentConversation,
  onSelect,
  onDelete,
  onCopy,
  onExport
}) => {
  const { language } = useLanguage();
  const [copiedId, setCopiedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [singleDeleteConv, setSingleDeleteConv] = useState(null); // {id, title} - para modal individual
  
  const isSpanish = language === 'es';
  
  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(new Set());
    }
  }, [isOpen]);
  
  // Clean up selectedIds when conversations change (after deletions)
  useEffect(() => {
    const currentConvIds = new Set(conversations.map(c => c.id));
    setSelectedIds(prev => {
      const validIds = new Set([...prev].filter(id => currentConvIds.has(id)));
      // Only update if there's a difference
      if (validIds.size !== prev.size) {
        return validIds;
      }
      return prev;
    });
  }, [conversations]);
  
  // Check if all are selected
  const allSelected = conversations.length > 0 && selectedIds.size === conversations.length;
  const someSelected = selectedIds.size > 0;
  
  // Toggle select all
  const handleSelectAll = (e) => {
    e.stopPropagation();
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(conversations.map(c => c.id)));
    }
  };
  
  // Toggle individual selection
  const handleToggleSelect = (convId, e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedIds);
    if (newSelected.has(convId)) {
      newSelected.delete(convId);
    } else {
      newSelected.add(convId);
    }
    setSelectedIds(newSelected);
  };
  
  // Delete all selected — abre modal React (no window.confirm que rompe el DOM en iframe)
  const handleDeleteSelected = (e) => {
    if (e) e.stopPropagation();
    if (selectedIds.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  // Ejecuta el borrado real tras confirmacion
  const confirmBulkDelete = async () => {
    const count = selectedIds.size;
    const idsToDelete = [...selectedIds];
    setShowBulkDeleteConfirm(false);

    if (count === 0) return;

    setIsDeleting(true);
    setSelectedIds(new Set());

    try {
      if (onDelete) {
        // skipConfirm=true: ya confirmamos con modal React; evitamos window.confirm que es bloqueado en iframes.
        await Promise.all(idsToDelete.map(id => onDelete(id, { skipConfirm: true })));
      }
      toast.success(isSpanish
        ? `${count} conversación${count > 1 ? 'es eliminadas' : ' eliminada'}`
        : `${count} conversation${count > 1 ? 's' : ''} deleted`
      );
    } catch (error) {
      console.error('Error deleting conversations:', error);
      toast.error(isSpanish ? 'Error al eliminar' : 'Error deleting');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleCopy = async (conv, e) => {
    e.stopPropagation();
    if (onCopy) {
      await onCopy(conv);
      setCopiedId(conv.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };
  
  const handleExport = (conv, e) => {
    e.stopPropagation();
    if (onExport) {
      onExport(conv);
    }
  };
  
  const handleDelete = (conv, e) => {
    e.stopPropagation();
    // Abre modal React de confirmacion individual (window.confirm es bloqueado en iframes).
    setSingleDeleteConv(conv);
  };

  // Confirma borrado individual con skipConfirm=true (ya confirmamos aqui).
  const confirmSingleDelete = async () => {
    const conv = singleDeleteConv;
    setSingleDeleteConv(null);
    if (!conv || !onDelete) return;
    try {
      await onDelete(conv.id, { skipConfirm: true });
    } catch (error) {
      console.error('Error deleting conversation:', error);
      const detail = error?.response?.data?.detail;
      toast.error(
        detail
          ? `${isSpanish ? 'Error al eliminar' : 'Error deleting'}: ${detail}`
          : (isSpanish ? 'Error al eliminar' : 'Error deleting')
      );
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onClick={onClose}
      data-testid="conversations-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-md mx-4 rounded-xl overflow-hidden shadow-2xl"
        style={{ 
          backgroundColor: '#0F172A',
          border: '1px solid #334155',
          boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="px-4 py-3 border-b flex items-center justify-between"
          style={{ backgroundColor: '#1E293B', borderColor: '#334155' }}
        >
          <div className="flex items-center gap-2">
            {/* Select All Checkbox */}
            {conversations.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="p-1 rounded hover:bg-slate-600 transition-colors"
                title={isSpanish ? 'Seleccionar todas' : 'Select all'}
                data-testid="select-all-checkbox"
              >
                {allSelected ? (
                  <CheckSquare className="w-4 h-4 text-indigo-400" />
                ) : someSelected ? (
                  <div className="relative">
                    <Square className="w-4 h-4 text-slate-400" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-0.5 bg-indigo-400 rounded" />
                    </div>
                  </div>
                ) : (
                  <Square className="w-4 h-4 text-slate-400" />
                )}
              </button>
            )}
            
            <MessageSquare className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-semibold text-white">
              {isSpanish ? 'Conversaciones Guardadas' : 'Saved Conversations'}
            </span>
            <span 
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ backgroundColor: '#3B82F620', color: '#3B82F6' }}
            >
              {conversations.length}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Delete Selected Button */}
            {someSelected && (
              <button
                onClick={handleDeleteSelected}
                disabled={isDeleting}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 text-red-400 text-xs font-medium transition-colors disabled:opacity-50"
                title={isSpanish ? 'Eliminar seleccionadas' : 'Delete selected'}
                data-testid="delete-selected-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{selectedIds.size}</span>
              </button>
            )}
            
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
              data-testid="close-conversations-modal"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Conversations List */}
        <div className="max-h-96 overflow-y-auto p-3 space-y-2">
          {conversations.length === 0 ? (
            <div className="py-12 text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="text-sm text-slate-400">
                {isSpanish ? 'Sin conversaciones' : 'No conversations'}
              </p>
            </div>
          ) : (
            conversations.map((conv, index) => {
              const isActive = currentConversation?.id === conv.id;
              const isSelected = selectedIds.has(conv.id);
              const preview = conv.messages?.[0]?.content?.slice(0, 60) || (isSpanish ? 'Nueva conversación' : 'New conversation');
              
              return (
                <div
                  key={conv.id}
                  onClick={() => {
                    onSelect(conv.id);
                    onClose();
                  }}
                  className={`p-3 rounded-lg cursor-pointer transition-all group ${
                    isActive 
                      ? 'bg-indigo-600/20 border border-indigo-500/40' 
                      : isSelected
                        ? 'bg-slate-700/50 border border-indigo-400/30'
                        : 'hover:bg-slate-800/80 border border-transparent'
                  }`}
                  style={{ backgroundColor: isActive || isSelected ? undefined : '#1E293B' }}
                  data-testid={`conversation-item-${index}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    {/* Checkbox */}
                    <button
                      onClick={(e) => handleToggleSelect(conv.id, e)}
                      className="p-1 rounded hover:bg-slate-600 transition-colors mt-0.5 flex-shrink-0"
                      data-testid={`conversation-checkbox-${index}`}
                    >
                      {isSelected ? (
                        <CheckSquare className="w-4 h-4 text-indigo-400" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-500 group-hover:text-slate-400" />
                      )}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="text-xs font-bold px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: '#3B82F620', color: '#60A5FA' }}
                        >
                          #{index + 1}
                        </span>
                        <span className="text-xs text-white font-medium truncate">
                          {conv.title || (isSpanish ? `Conversación ${index + 1}` : `Conversation ${index + 1}`)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 truncate">{preview}...</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] text-slate-500">
                          {conv.message_count || conv.messages?.length || 0} {isSpanish ? 'mensajes' : 'messages'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons - Always visible */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={(e) => handleCopy(conv, e)}
                        className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600 transition-colors"
                        title={isSpanish ? 'Copiar' : 'Copy'}
                        data-testid={`copy-conv-${index}`}
                      >
                        {copiedId === conv.id ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-300" />
                        )}
                      </button>
                      <button
                        onClick={(e) => handleExport(conv, e)}
                        className="p-2 rounded-lg bg-green-600/30 hover:bg-green-600/50 transition-colors"
                        title={isSpanish ? 'Salvar' : 'Save'}
                        data-testid={`save-conv-${index}`}
                      >
                        <Save className="w-4 h-4 text-green-400" />
                      </button>
                      <button
                        onClick={(e) => handleExport(conv, e)}
                        className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600 transition-colors"
                        title={isSpanish ? 'Exportar' : 'Export'}
                        data-testid={`export-conv-${index}`}
                      >
                        <FileDown className="w-4 h-4 text-slate-300" />
                      </button>
                      <button
                        onClick={(e) => handleDelete(conv, e)}
                        className="p-2 rounded-lg bg-red-600/20 hover:bg-red-600/40 transition-colors"
                        title={isSpanish ? 'Eliminar' : 'Delete'}
                        data-testid={`delete-conv-${index}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {/* Footer hint when items are selected */}
        {someSelected && (
          <div 
            className="px-4 py-2 border-t text-xs text-center text-slate-400"
            style={{ backgroundColor: '#1E293B', borderColor: '#334155' }}
          >
            {isSpanish 
              ? `${selectedIds.size} conversación${selectedIds.size > 1 ? 'es' : ''} seleccionada${selectedIds.size > 1 ? 's' : ''}` 
              : `${selectedIds.size} conversation${selectedIds.size > 1 ? 's' : ''} selected`
            }
          </div>
        )}
      </div>

      {/* Modal de confirmacion de borrado masivo (reemplaza window.confirm) */}
      {showBulkDeleteConfirm && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); setShowBulkDeleteConfirm(false); }}
          data-testid="bulk-delete-conversations-confirm"
        >
          <div
            className="bg-zinc-800 border border-zinc-700 rounded-xl p-5 max-w-sm mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-white mb-2">
              {isSpanish
                ? `¿Eliminar ${selectedIds.size} conversación${selectedIds.size > 1 ? 'es' : ''}?`
                : `Delete ${selectedIds.size} conversation${selectedIds.size > 1 ? 's' : ''}?`}
            </h3>
            <p className="text-sm text-zinc-400 mb-4">
              {isSpanish
                ? 'Esta acción no se puede deshacer.'
                : 'This action cannot be undone.'}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                data-testid="cancel-bulk-delete-conversations"
              >
                {isSpanish ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={confirmBulkDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
                data-testid="confirm-bulk-delete-conversations"
              >
                {isSpanish ? 'Eliminar' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmacion individual (reemplaza window.confirm que es bloqueado en iframes) */}
      {singleDeleteConv && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); setSingleDeleteConv(null); }}
          data-testid="single-delete-conversation-confirm"
        >
          <div
            className="bg-zinc-800 border border-zinc-700 rounded-xl p-5 max-w-sm mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-white mb-2">
              {isSpanish ? '¿Enviar a la Papelera?' : 'Send to Trash?'}
            </h3>
            <p className="text-sm text-zinc-400 mb-4">
              {isSpanish
                ? 'Esta conversación se enviará a la Papelera y podrás recuperarla durante 30 días. Los usuarios Free deben pagar para recuperarla.'
                : 'This conversation will be sent to Trash and can be recovered within 30 days. Free users must pay to recover it.'}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setSingleDeleteConv(null)}
                className="px-4 py-2 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                data-testid="cancel-single-delete-conversation"
              >
                {isSpanish ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={confirmSingleDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
                data-testid="confirm-single-delete-conversation"
              >
                {isSpanish ? 'Eliminar' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ConversationsModal;
